import { useCallback, useEffect, useRef, useState } from "react";
import { api, type LanguagesView } from "../api.js";
import { BILLING_CHANGED } from "../components/BillingChip.js";
import { anyInProgress, finishedSince } from "../languages/languages.js";

const POLL_MS = 5_000;

/**
 * The languages of one launch version. It is read again when the version
 * changes, and while one is being made, unless `paused`, which is when the
 * Languages panel is open and polls for itself. The view is the selected
 * version's or nothing: another version's languages are never handed back.
 */
export function useVersionLanguages(projectId: string, versionId: string | null, paused: boolean) {
  const [read, setRead] = useState<{ versionId: string; view: LanguagesView } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const newest = useRef(0);
  const before = useRef<{ versionId: string; languages: LanguagesView["languages"] } | null>(null);

  const reload = useCallback(() => {
    if (!versionId) return;
    const mine = ++newest.current;
    setFailed(null);
    void api
      .languages(projectId, versionId)
      .then((next) => {
        if (mine !== newest.current) return;
        // A language was charged, or its hold let go: the balance moved.
        const last = before.current?.versionId === versionId ? before.current.languages : null;
        if (finishedSince(last, next.languages)) window.dispatchEvent(new Event(BILLING_CHANGED));
        before.current = { versionId, languages: next.languages };
        setRead({ versionId, view: next });
      })
      .catch(() => {
        if (mine === newest.current) setFailed(versionId);
      });
  }, [projectId, versionId]);

  useEffect(reload, [reload]);

  const view = read && read.versionId === versionId ? read.view : null;
  const polling = anyInProgress(view) && !paused;
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => latest.current(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling]);

  return { view, reload, failed: view === null && failed === versionId };
}
